import { useI18n } from 'vue-i18n'
import { formatMoney, formatRate, formatRateBeside, yerevanDate } from '@molvia/model'
import type { ExchangeRate, ExchangeView } from '@molvia/model'
import { signedAmount } from '@/components/accounts'
import { bankWords } from '@/components/spending'
import { calendarDay, calendarDayOfAnyYear } from '@/days'

/** What the card of an exchange says of the central bank of its day, each part on its own line. */
export interface OfficialWords {
  /** «ЦБ РА на 19 сент.» — or the open source named instead, never beside it (MOL-42, С-6). */
  readonly label: string
  readonly rate: string
  /** «На 1 603,33 ₽ больше, чем по ЦБ РА» — a difference, never a commission (MOL-40, В-3). */
  readonly difference: string
}

/**
 * One market figure beside an exchange (MOL-137): whose it is and the day, the rate, the
 * difference — and its tone, since against the market «хуже / лучше» is what the line means (Ф-30).
 */
export interface MarketLineWords {
  readonly label: string
  readonly rate: string
  /** «−416,25 ₽ · хуже, чем в банках безналом» — the sign and the word beside the colour. */
  readonly difference: string
  /** Below zero the exchange gave less than the market would have; nothing has no tone. */
  readonly tone: 'bad' | 'good' | null
}

/** The market lines of a card: the best of the day, the person's own channel, what is to come. */
export interface MarketWords {
  readonly best: MarketLineWords
  readonly own: MarketLineWords | null
  readonly pending: string | null
}

type MarketQuoteView = NonNullable<ExchangeView['market']>['best']

export interface ExchangeWords {
  readonly rateOf: (rate: ExchangeRate) => string
  readonly day: (date: string) => string
  readonly dayOf: (exchange: ExchangeView) => string
  readonly moneyOf: (value: ExchangeView['given']) => string
  readonly amountsOf: (exchange: ExchangeView) => string
  readonly rateLineOf: (exchange: ExchangeView, today: string) => string
  readonly officialOf: (exchange: ExchangeView) => OfficialWords | null
  readonly noOfficialOf: (exchange: ExchangeView) => string
  /** The bank an exchange's pair is measured by, named — «ЦБ РА», «НБ Грузии» (MOL-110). */
  readonly bankOf: (exchange: ExchangeView) => string
  readonly marketOf: (exchange: ExchangeView) => MarketWords | null
}

/**
 * The words «Обмен денег» says an exchange in — on its card, in the strip after a removal and in
 * «Удалить обмен?» alike (MOL-81). Every figure is the server's; this only chooses the words.
 */
export function useExchangeWords(): ExchangeWords {
  const { t, locale } = useI18n()

  const rateOf = (rate: ExchangeRate): string => formatRate(rate, locale.value)
  /**
   * A day of Yerevan printed as that calendar day, never as the moment of its midnight — west of
   * UTC+4 every exchange came out a day early, as the spendings of «Деньги» once did (review Т-1 of
   * MOL-82, adversarial Ж).
   */
  const day = (date: string): string =>
    calendarDay(date, locale.value, { day: 'numeric', month: 'short' })
  const dayOf = (exchange: ExchangeView): string => day(exchange.exchangedOn)
  /** Amounts as they were typed, as everywhere in «Деньги» (owner's decision В-1). */
  const moneyOf = (value: ExchangeView['given']): string => formatMoney(value, locale.value)

  function amountsOf(exchange: ExchangeView): string {
    return t('exchange.row_amounts', {
      given: moneyOf(exchange.given),
      received: moneyOf(exchange.received),
    })
  }

  /**
   * The day and the rate it was made at — the day alone for amounts no rate in the band says. «Удалить обмен?» has
   * no month's caption over it, so a day of another year says its year (MOL-329, adversarial Ж1).
   */
  function rateLineOf(exchange: ExchangeView, today: string): string {
    const date = calendarDayOfAnyYear(exchange.exchangedOn, locale.value, today)
    return exchange.rate ? t('exchange.row_rate', { date, rate: rateOf(exchange.rate) }) : date
  }

  function officialOf(exchange: ExchangeView): OfficialWords | null {
    const official = exchange.official
    if (!official) return null
    const minor = official.difference.minor
    // A name to stand at the head of a line and after «the» alike (adversarial Е).
    const source = t(`exchange.card_source_${official.provider}`)
    const words = {
      amount: moneyOf({ ...official.difference, minor: minor < 0n ? -minor : minor }),
      source,
    }
    const bank = official.provider === 'cba'
    const key = minor > 0n ? 'more' : minor < 0n ? 'less' : 'equal'
    return {
      label: t('exchange.card_official', { source, date: day(yerevanDate(official.rate.asOf)) }),
      // On the side of the exchange's own rate above it, even under one (adversarial Г).
      rate: exchange.rate
        ? formatRateBeside(official.rate, exchange.rate, locale.value)
        : rateOf(official.rate),
      difference: t(`exchange.card_${key}${bank ? '' : '_other'}`, words),
    }
  }

  /** Why there is nothing to compare with — no rate of that day, or one in doubt (MOL-40, С-5). */
  function noOfficialOf(exchange: ExchangeView): string {
    return t(exchange.officialDoubtful ? 'exchange.card_doubtful' : 'exchange.card_no_official', {
      bank: bankOf(exchange),
    })
  }

  /** The bank the pair of an exchange is measured by, named (MOL-110). */
  function bankOf(exchange: ExchangeView): string {
    return bankWords(exchange.given.currency, exchange.received.currency, t)
  }

  /**
   * A market figure in words: the row it came from — «все клиенты банков» where that stood in for
   * non-cash (В-2) — its day, the rate on the side of the exchange's own, and the difference with
   * its sign and «хуже / лучше» (Ф-30): below zero the exchange gave less in the received currency.
   */
  function marketLineOf(
    exchange: ExchangeView,
    quote: MarketQuoteView,
    key: 'best' | 'own',
  ): MarketLineWords {
    const minor = quote.difference.minor
    const amount = signedAmount(quote.difference, locale.value, { plus: true })
    const where = t(`exchange.market.where_${quote.basis}`)
    const sign = minor > 0n ? 'better' : minor < 0n ? 'worse' : 'equal'
    return {
      label: t(`exchange.market.${key}`, {
        channel: t(`exchange.channel.${quote.basis}`),
        date: day(yerevanDate(quote.rate.asOf)),
      }),
      rate: exchange.rate
        ? formatRateBeside(quote.rate, exchange.rate, locale.value)
        : rateOf(quote.rate),
      difference: t(`exchange.market.${sign}`, { amount, where }),
      tone: minor > 0n ? 'good' : minor < 0n ? 'bad' : null,
    }
  }

  /** The exchange against the market of its day (MOL-137, В-1) — null where there is none (В-4). */
  function marketOf(exchange: ExchangeView): MarketWords | null {
    const market = exchange.market
    if (!market) return null
    return {
      best: marketLineOf(exchange, market.best, 'best'),
      own: market.own ? marketLineOf(exchange, market.own, 'own') : null,
      pending: market.exchangersPending ? t('exchange.market.pending') : null,
    }
  }

  return {
    rateOf,
    day,
    dayOf,
    moneyOf,
    amountsOf,
    rateLineOf,
    officialOf,
    noOfficialOf,
    bankOf,
    marketOf,
  }
}
